import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Tab,
  Tabs,
} from "@mui/material";
import { FormProvider, UseFormReturn, useForm } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFTextField,
} from "../../../../../../../RHFComponents/RHFComponents";
import { pgmedp, pgmedtage } from "../../../../../../../../networking";
import { MedintDataContext } from "../../../../../../Context/MedintContextWrapper";
import { useLinkedAutocompleteOptions } from "../../../../../../../../useLinkedAutoCompleteOptions";
import { PGMEDP } from "../../../../../../../../services/medintAgendaFormTypes";
import { AllertMessage } from "../MedintAgendaFormDetail";

type PrestazioniForm = {
  prestazioniAbilitateForm: {
    specialita: string;
    prestazione: string;
    tipologia: string;
    numMaxPrest: string;
    durataStand: string;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  action: "insert" | "edit" | "copy";
  initialData?: PGMEDP;
  parentMethods: UseFormReturn<any>;
  selectedOrarioId: string | null;
  createdIdPgmedt?: string;
};

const buildDefault = (): PrestazioniForm => ({
  prestazioniAbilitateForm: {
    specialita: "",
    prestazione: "",
    tipologia: "",
    numMaxPrest: "0",
    durataStand: "0",
  },
});

const PrestazioniAbilitateModal = (props: Props) => {
  const {
    open,
    onClose,
    action,
    initialData,
    parentMethods,
    selectedOrarioId,
    createdIdPgmedt,
  } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<PrestazioniForm | null>(
    null,
  );

  const modalMethods = useForm<PrestazioniForm>({
    defaultValues: buildDefault(),
    mode: "onChange",
  });

  const context = useContext(MedintDataContext);

  //Recupero le specialità e le prestazioni dal context
  const specOptions = context?.specialitiesSPECIALITA ?? [];
  const prestOptions = context?.performancePRESTAZIONI ?? [];

  // Chiamo l'hook useLinkedAutocompleteOptions per filtrare le prestazioni e le specialità
  // in base alla selezione dell'utente
  const {
    filteredLeftOptions: filteredSpecOptions,
    filteredRightOptions: filteredPrestOptions,
  } = useLinkedAutocompleteOptions({
    methods: modalMethods,

    leftName: "prestazioniAbilitateForm.specialita",
    rightName: "prestazioniAbilitateForm.prestazione",

    leftOptions: specOptions,
    rightOptions: prestOptions,

    leftKey: (s: any) => String(s.SPECIALITA ?? ""),
    rightKey: (p: any) => String(p.PRESTAZIONE ?? ""),

    rightToLeftKey: (p: any) => String(p.SPECIALITA ?? ""),
  });

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  // Riempio il modale con i valori di default se sono in edit
  useEffect(() => {
    if (!open) return;

    setError(null);

    if (action === "insert") {
      modalMethods.reset(buildDefault());
      return;
    }

    modalMethods.reset({
      prestazioniAbilitateForm: {
        specialita: initialData?.CODICESPECIALITA ?? "",
        prestazione: initialData?.CODICEPRESTAZIONE ?? "",
        tipologia: "",
        numMaxPrest: String(initialData?.MAXESAMI ?? "0"),
        durataStand: String(initialData?.DURATASTD ?? "0"),
      },
    });
  }, [open, action, initialData, modalMethods]);

  const refreshAgenda = async () => {
    const idPgmedt =
      parentMethods.getValues("medIntNuovoProgramma.chiave") ||
      (createdIdPgmedt as string | undefined);
    if (!idPgmedt)
      throw new Error("Manca la chiave agenda (medIntNuovoProgramma.chiave).");

    const response = await pgmedtage({ crud: "R", codicePgmedt: "", idPgmedt });

    parentMethods.setValue(
      "medIntNuovoProgramma.tipologie",
      response?.data?.dati?.[0]?.tipoPrenotazione ?? [],
      { shouldDirty: true, shouldTouch: false, shouldValidate: false },
    );
  };

  const askConfirmSubmit = modalMethods.handleSubmit((values) => {
    setPendingSubmit(values);

    setSeverity("warning");
    setMessage(
      action === "insert"
        ? "Confermi l'inserimento della prestazione?"
        : action === "edit"
          ? "Confermi la modifica della prestazione?"
          : "Confermi la copia della prestazione?",
    );
    setShowAllert(true);
  });

  const submitPrestazione = async () => {
    if (!pendingSubmit) return;

    setLoading(true);

    try {
      const idPgmedt =
        parentMethods.getValues("medIntNuovoProgramma.chiave") ||
        (createdIdPgmedt as string | undefined);
      if (!idPgmedt) throw new Error("Manca chiave agenda");

      if (action === "insert" && !selectedOrarioId) {
        throw new Error(
          "Seleziona prima un orario fisso (serve per inserire una prestazione).",
        );
      }

      const crud = action === "insert" ? "c" : "u";
      const formValues = pendingSubmit.prestazioniAbilitateForm;

      if (action !== "insert" && !initialData?.IDPGMEDP) {
        throw new Error("Manca ID prestazione (IDPGMEDP) per la modifica.");
      }

      const response = await pgmedp({
        crud,
        ...(action !== "insert" ? { prgint: initialData?.IDPGMEDP } : {}),
        dati: {
          pgmedp: {
            PGMEDO_PRGINT: selectedOrarioId,
            SPECIA_CODICE: formValues.specialita,
            ANAPRE_COD: formValues.prestazione,
            PGMEDP_NMAX: String(formValues.numMaxPrest ?? "0"),
            PGMEDG_DURATA_STD: String(formValues.durataStand ?? "0"),
            TPGPRE_CODICE: formValues.tipologia,
            NORMALIZZA: "",
          },
        },
      });

      if (response?.data?.esito === "OK") {
        setLoading(false);
        setSeverity("success");
        setMessage("Operazione completata con successo.");
      } else {
        setSeverity("error");
        setMessage("Salvataggio non riuscito.");
        return;
      }

      await refreshAgenda();
      setTimeout(() => onClose(), 800);
    } catch (e: any) {
      console.error("Errore salvataggio prestazione:", e);
      setSeverity("error");
      setMessage(e?.message ?? "Errore durante il salvataggio.");
    } finally {
      setPendingSubmit(null);
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={severity === "warning" ? submitPrestazione : null}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal
        open={open}
        onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          onClose();
        }}
      >
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "150vh",
            width: "50%",
            overflow: "auto",
            paddingTop: 1,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <FormProvider {...modalMethods}>
            <Tabs value={0} sx={{ paddingLeft: 2 }}>
              <Tab
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                label="Prestazioni abilitate nell'orario - Dettaglio"
              />
            </Tabs>

            <Grid container spacing={2} padding={2}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <RHFAutocomplete
                  name="prestazioniAbilitateForm.specialita"
                  control={modalMethods.control}
                  fullWidth
                  label="Specialità"
                  options={filteredSpecOptions}
                  getOptionLabel={(t: any) => `${t.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.SPECIALITA).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.SPECIALITA ?? "")}
                />
              </Grid>

              <Grid item xs={12}>
                <RHFAutocomplete
                  name="prestazioniAbilitateForm.prestazione"
                  control={modalMethods.control}
                  fullWidth
                  label="Prestazione"
                  options={filteredPrestOptions}
                  getOptionLabel={(t: any) => `${t.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.PRESTAZIONE).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.PRESTAZIONE ?? "")}
                />
              </Grid>

              <Grid item xs={12}>
                <RHFAutocomplete
                  name="prestazioniAbilitateForm.tipologia"
                  control={modalMethods.control}
                  fullWidth
                  label="Tipologia"
                  options={context?.typologiesTIPOLOGIE ?? []}
                  getOptionLabel={(t: any) => `${t.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.CODICE).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.CODICE ?? "")}
                />
              </Grid>

              <Grid item xs={6}>
                <RHFTextField
                  name="prestazioniAbilitateForm.numMaxPrest"
                  control={modalMethods.control}
                  label="Numero max prestazioni"
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <RHFTextField
                  name="prestazioniAbilitateForm.durataStand"
                  control={modalMethods.control}
                  label="Durata standard"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Button
                    variant="outlinedCustom"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Indietro
                  </Button>

                  <Button
                    variant="containedCustom"
                    disabled={loading}
                    onClick={askConfirmSubmit}
                  >
                    Conferma
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default PrestazioniAbilitateModal;
